import { useCallback, useEffect, useRef, useState } from 'react';
import type * as Ably from 'ably';

import {
  acquireRealtimeClient,
  releaseRealtimeClient,
  waitForConnected,
  type GetAccessToken
} from './realtime-client';
import {
  subscribeToChannelRoot,
  type ChannelSubscription
} from './channel-root';
import { fetchSnapshot } from '@lib/live/match/snapshot';
import {
  channelNameForMatch,
  channelNameForPlayer,
  projectMainChannel,
  projectPlayerChannel
} from '@lib/live/match/channels';
import type {
  ConnectionPhase,
  LivePlayer,
  MatchSnapshot,
  MatchState
} from '@lib/live/match/types';

/**
 * A failed attach would otherwise leave the match in a terminal ERROR phase
 * until remount, silently freezing the tile's points. LiveObjects resyncs a
 * live connection by itself, so this only covers a failure to get attached in
 * the first place.
 */
const ATTACH_RETRY_DELAYS_MS = [1_000, 3_000, 8_000];

/**
 * How many times a channel may fail before the pitch gives up on it.
 *
 * A failed channel does not recover on its own, so it has to be re-attached,
 * and re-attaching costs a snapshot read and a token mint. Left unbounded, a
 * channel failing for a permanent reason (a capability the token does not
 * grant, say) would loop on that cost and burn the endpoint's per-user mint
 * budget until it started refusing.
 */
const MAX_CHANNEL_FAILURE_REATTACHES = 3;

/** Statuses that mean the ball is in play, for the pitch's live badge. */
const IN_PLAY_STATUSES = new Set(['LIVE', 'SECOND_HALF', 'HALF_TIME']);

/**
 * Statuses with no realtime channel worth attaching to.
 *
 * `SCHEDULED` is before the engine's bootstrap publish, so the channels hold
 * nothing yet. `POSTPONED` and `ABANDONED` are terminal without ever being
 * settled: attaching would replace the snapshot's authoritative status with
 * the empty root's default of SCHEDULED, and a postponed fixture would then
 * outrank a real one when a double gameweek picks which to show.
 */
const NON_REALTIME_STATUSES = new Set(['SCHEDULED', 'POSTPONED', 'ABANDONED']);

type UseMatchConnectionResult = {
  state: MatchState;
  connectionPhase: ConnectionPhase;
  error: string | null;
  isLive: boolean;
  /**
   * Whether this fixture's match-level state has arrived, from either source.
   *
   * The auto-sub rules read a missing player as "left out of the squad", which
   * is only a safe reading once something has actually been delivered for the
   * fixture. Before that, absence just means the wire has not caught up.
   */
  hasMainSynced: boolean;
};

type UseMatchConnectionOptions = {
  matchId: string;
  /**
   * Opta ids of the squad players featuring in this match. Only these player
   * channels are attached: a match has about forty, and the pitch renders at
   * most a handful of them.
   */
  playerOptaIds: string[];
  getAccessToken: GetAccessToken;
};

/**
 * Whether a match has a realtime feed worth attaching to.
 *
 * Finalised, not yet bootstrapped, or terminal-without-settlement all mean the
 * snapshot is the only source there is. Attaching anyway would overwrite its
 * authoritative status with an empty root's defaults.
 */
export const shouldAttachRealtime = (snapshot: MatchSnapshot): boolean =>
  !snapshot.isFinalised && !NON_REALTIME_STATUSES.has(snapshot.status);

const emptyState = (matchId: string): MatchState => ({
  matchId,
  status: 'SCHEDULED',
  kickoffAt: null,
  players: {},
  isFinalised: false,
  fplSettledAt: null,
  provenance: null
});

/** Project the REST snapshot onto the same state the channels produce. */
export const stateFromSnapshot = (
  matchId: string,
  snapshot: MatchSnapshot
): MatchState => {
  const players: Record<string, LivePlayer> = {};
  for (const player of snapshot.players) {
    players[player.playerId] = {
      playerId: player.playerId,
      fplId: player.fplId,
      isStarter: player.isStarter,
      isOnPitch: player.isOnPitch,
      minutesPlayed: player.minutesPlayed,
      points: player.points,
      bps: player.bps,
      scoring: player.scoring,
      generalStats: player.generalStats
    };
  }
  return {
    matchId,
    status: snapshot.status,
    kickoffAt: snapshot.kickoffAt,
    players,
    isFinalised: snapshot.isFinalised === true,
    fplSettledAt: snapshot.fplSettledAt,
    // The snapshot is asserted rather than validated, so an engine revision
    // that does not carry the field yet arrives as undefined rather than null.
    provenance: snapshot.provenance ?? null
  };
};

/**
 * Subscribe to one match's LiveObjects channels and expose the slice of state
 * the pitch renders.
 *
 * A match that is already finalised, or has not kicked off, is served from the
 * REST snapshot and never attaches. That is not leftover bootstrap machinery:
 * LiveObjects state lives with the channel, and a channel that has wound down
 * after full time has nothing left to hand a client on attach. The snapshot is
 * the only source that still answers for a match played last Saturday, which
 * is most of what the my-team points page is asked to show.
 *
 * For a match in play, the attach itself is the bootstrap. Objects mode
 * delivers full state on attach and streams operations after it, so the
 * snapshot-then-replay hydration this hook used to run, along with its version
 * cursor, event buffer, stale-snapshot retries and heartbeat staleness clock,
 * has no remaining purpose.
 */
export const useMatchConnection = ({
  matchId,
  playerOptaIds,
  getAccessToken
}: UseMatchConnectionOptions): UseMatchConnectionResult => {
  const [state, setState] = useState<MatchState>(() => emptyState(matchId));
  const [connectionPhase, setConnectionPhase] =
    useState<ConnectionPhase>('IDLE');
  const [error, setError] = useState<string | null>(null);
  const [hasMainSynced, setHasMainSynced] = useState(false);

  const clientRef = useRef<Ably.Realtime | null>(null);
  const subscriptionsRef = useRef<ChannelSubscription[]>([]);
  // Identifies the current effect run. A run that has been superseded must not
  // keep acquiring the shared client or attaching channels: only the newest
  // run's teardown is ever called, so anything a stale run acquires is never
  // given back. Incremented on every setup and teardown.
  const generationRef = useRef(0);
  const attemptRef = useRef(0);
  const failureReattachRef = useRef(0);
  const pendingRetryRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stash the getter so the attach closure sees the current token without the
  // effect re-running whenever the provider re-renders.
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;

  // Compare by value: the provider rebuilds this array on every render, so an
  // identity check would tear down and re-attach every channel each time.
  const playerOptaIdsKey = playerOptaIds.join(',');

  /**
   * A channel that delivers again has recovered, whatever it reported when it
   * dropped out. Without this the phase set by `onUnavailable` would stick for
   * the rest of the session and the pitch would stay un-badged while state was
   * flowing, which is the opposite of the problem that flag exists to solve.
   *
   * Only RECONNECTING is cleared: FINALISED and PRE_MATCH are settled states
   * that never attached, and promoting them to LIVE would be a lie.
   */
  const markDelivering = useCallback(() => {
    setConnectionPhase((previous) =>
      previous === 'RECONNECTING' ? 'LIVE' : previous
    );
  }, []);

  const applyMainChannel = useCallback(
    (raw: Record<string, unknown>) => {
      const main = projectMainChannel(raw);
      setState((previous) => ({ ...previous, ...main }));
      setHasMainSynced(true);
      markDelivering();
    },
    [markDelivering]
  );

  const applyPlayerChannel = useCallback(
    (optaId: string, raw: Record<string, unknown>) => {
      const player = projectPlayerChannel(raw, optaId);
      setState((previous) => ({
        ...previous,
        players: { ...previous.players, [optaId]: player }
      }));
      markDelivering();
    },
    [markDelivering]
  );

  // Set once `connect` exists, so `onUnavailable` can ask for a re-attach
  // without the two closing over each other.
  const reattachRef = useRef<(() => void) | null>(null);

  const closeSubscriptions = useCallback(() => {
    for (const subscription of subscriptionsRef.current) {
      subscription.close();
    }
    subscriptionsRef.current = [];
  }, []);

  const connect = useCallback(async () => {
    const generation = generationRef.current;
    const isStale = () => generation !== generationRef.current;

    try {
      setError(null);
      setConnectionPhase('ATTACHING');

      const accessToken = await getAccessTokenRef.current();
      const preflight = await fetchSnapshot(matchId, accessToken);

      if (isStale()) return;

      // Seed from the snapshot whatever happens next. It is the authoritative
      // status for a match with no realtime feed, and for one that does have
      // it, it costs a render and is immediately superseded by state-on-attach.
      setState(stateFromSnapshot(matchId, preflight));
      setHasMainSynced(true);

      if (!shouldAttachRealtime(preflight)) {
        attemptRef.current = 0;
        setConnectionPhase(preflight.isFinalised ? 'FINALISED' : 'PRE_MATCH');
        return;
      }

      // Acquire once per run. A retry re-enters this function, and acquiring
      // again would take a second refcount that nothing gives back, leaving the
      // connection open for the rest of the session.
      const client =
        clientRef.current ??
        (await acquireRealtimeClient(matchId, getAccessTokenRef.current));

      if (isStale()) {
        // This run acquired the client but its teardown has already run, so
        // nothing else will hand this refcount back.
        if (!clientRef.current) releaseRealtimeClient(matchId);
        return;
      }

      clientRef.current = client;
      client.connect();
      await waitForConnected(client);

      if (isStale()) return;

      const optaIds = playerOptaIdsKey ? playerOptaIdsKey.split(',') : [];

      const onUnavailable = (channelState: string) => {
        if (isStale()) return;
        // Points stop moving from here. Say so rather than leaving the pitch
        // badged live against values that will never change again.
        console.warn(`[live] channel ${channelState} for match ${matchId}`);
        setConnectionPhase('RECONNECTING');

        // `suspended` recovers by itself: the SDK reattaches, LiveObjects
        // resyncs, and the next delivery clears the phase. `failed` is terminal
        // in Ably, so nothing arrives to clear it and the pitch would sit frozen
        // until the component remounted. Re-attach instead, which is what the
        // pre-LiveObjects implementation did on the same signal.
        if (channelState !== 'failed') return;

        if (failureReattachRef.current >= MAX_CHANNEL_FAILURE_REATTACHES) {
          console.error(
            `[live] channel keeps failing for match ${matchId}; giving up`
          );
          setError('Live updates are unavailable for this match');
          setConnectionPhase('ERROR');
          return;
        }

        failureReattachRef.current += 1;
        reattachRef.current?.();
      };

      // Settled rather than all: a rejection from one channel would otherwise
      // orphan the channels that did attach, leaving them subscribed and
      // costing messages with nothing holding a handle to detach them.
      const results = await Promise.allSettled([
        subscribeToChannelRoot(client, channelNameForMatch(matchId), {
          onSnapshot: (raw) => {
            if (!isStale()) applyMainChannel(raw);
          },
          onUnavailable
        }),
        ...optaIds.map((optaId) =>
          subscribeToChannelRoot(
            client,
            channelNameForPlayer(matchId, optaId),
            {
              onSnapshot: (raw) => {
                if (!isStale()) applyPlayerChannel(optaId, raw);
              },
              onUnavailable
            }
          )
        )
      ]);

      const subscriptions = results
        .filter(
          (result): result is PromiseFulfilledResult<ChannelSubscription> =>
            result.status === 'fulfilled'
        )
        .map((result) => result.value);

      const failure = results.find((result) => result.status === 'rejected');

      if (isStale() || failure) {
        for (const subscription of subscriptions) subscription.close();
        if (failure && !isStale()) {
          throw failure.reason instanceof Error
            ? failure.reason
            : new Error(String(failure.reason));
        }
        return;
      }

      subscriptionsRef.current = subscriptions;
      attemptRef.current = 0;
      setConnectionPhase('LIVE');
    } catch (caughtError) {
      if (isStale()) return;

      const message =
        caughtError instanceof Error ? caughtError.message : 'Unknown error';
      const attempt = attemptRef.current;

      if (attempt < ATTACH_RETRY_DELAYS_MS.length) {
        const retryDelay = ATTACH_RETRY_DELAYS_MS[attempt];
        attemptRef.current = attempt + 1;
        // Not necessarily the attach: this covers the token, the snapshot and
        // the connection too, so name the match and let the message say which.
        console.warn(
          `[live] match ${matchId} could not be connected (${message}); retry ${
            attempt + 1
          }/${ATTACH_RETRY_DELAYS_MS.length} in ${retryDelay}ms`
        );
        pendingRetryRef.current = setTimeout(() => {
          pendingRetryRef.current = null;
          void connect();
        }, retryDelay);
        return;
      }

      setError(message);
      setConnectionPhase('ERROR');
    }
  }, [matchId, playerOptaIdsKey, applyMainChannel, applyPlayerChannel]);

  // Drop the dead subscriptions and start a fresh run. Superseding the
  // generation stops the failed run's callbacks from touching state, and the
  // client is reused: a failed channel does not mean a failed connection.
  const reattach = useCallback(() => {
    generationRef.current += 1;
    closeSubscriptions();
    attemptRef.current = 0;
    void connect();
  }, [closeSubscriptions, connect]);

  reattachRef.current = reattach;

  useEffect(() => {
    generationRef.current += 1;
    // Each run gets the full retry budget. Carrying a previous run's exhausted
    // count over would send the next one straight to ERROR on its first stumble.
    attemptRef.current = 0;
    failureReattachRef.current = 0;
    void connect();

    return () => {
      // Supersede this run so anything still in flight stops short.
      generationRef.current += 1;
      if (pendingRetryRef.current) {
        clearTimeout(pendingRetryRef.current);
        pendingRetryRef.current = null;
      }
      closeSubscriptions();
      if (clientRef.current) {
        releaseRealtimeClient(matchId);
        clientRef.current = null;
      }
    };
  }, [connect, closeSubscriptions, matchId]);

  return {
    state,
    connectionPhase,
    error,
    isLive: connectionPhase === 'LIVE' && IN_PLAY_STATUSES.has(state.status),
    hasMainSynced
  };
};
